import React from "react";
import PropTypes from "prop-types";
import ImmutablePropTypes from "react-immutable-proptypes";

import Checkbox from "components/Checkbox";
import Dropdown from "components/Dropdown";
import Currency from "components/Currency";

// possible values for the type prop
export const inputTypes = {
  CHECKBOXES: "checkboxes",
  RADIO_BUTTONS: "radio_buttons",
  DROPDOWN: "dropdown"
}

/**
 * Intended to be used as a child of WeekdayTable
 */
export default class DailyBookingInput extends React.Component {
  
  static types = inputTypes;
  
  static propTypes = {
    options: ImmutablePropTypes.listOf(ImmutablePropTypes.contains({
      id: PropTypes.any.isRequired,
      name: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired
    })).isRequired,
    selectedOptionID: PropTypes.string,
    onChange: PropTypes.func,
    type: PropTypes.oneOf(Object.values(inputTypes)),
    cssBlockName: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    disabledPlaceholder: PropTypes.node,
    readOnly: PropTypes.bool
  }
  
  static defaultProps = {
    type: inputTypes.DROPDOWN,
    disabled: false,
    readOnly: false
  }
  
  getSelectedOptionID() {
    return this.props.selectedOptionID;
  }
  
  getOptionByID(optionID) {
    return this.props.options.find(option => option.get("id").toString() === optionID) || null;
  }
  
  getSelectedOption() {
    return this.getOptionByID(this.getSelectedOptionID());
  }
  
  isOptionSelected() {
    return this.getSelectedOption() !== null;
  }
  
  /**
   * Get the price of the selected option
   */
  getSelectedOptionPrice() {
    return this.isOptionSelected() ? this.getSelectedOption().get("price") : 0;
  }
  
  /**
   * We need to detect if the user has tried to make a change when the option is disabled/readOnly
   */
  onInputClick = () => {
    if (this.props.readOnly) {
      this.props.onReadOnlyClick();
    }
  }
  
  /**
   * When an option is changed, update the state and fire a callback
   */
  onChange(selectedOptionID) {
    const previousOptionID = this.getSelectedOptionID();
    const { disabled, readOnly } = this.props;
    if (this.props.onChange && !disabled && !readOnly) {
      this.props.onChange(selectedOptionID, previousOptionID);
    }
  }
  
  /**
   * When a checkbox is un/checked, update the state
   */
  onCheckboxChange = (optionID, checked) => {
    if (checked) {
      this.onChange(optionID);
    } else {
      this.onChange("");
    }
  }
  
  /**
   * When a radio button is checked, update the state
   */
  onRadioButtonChange = (optionID) => {
    this.onChange(optionID);
  }
  
  /**
   * When a dropdown is changed, update the state
   */
  onDropdownChange = (selectedOptionID) => {
    this.onChange(selectedOptionID);
  }
  
  renderCheckboxes() {
    const { options } = this.props;
    return options.map(option => {
      const id = option.get("id");
      const selected = id === this.getSelectedOptionID();
      return (
        <Checkbox
          cssModifier="school_money"
          checked={selected}
          onChange={(checked) => this.onCheckboxChange(id, checked)}
        />
      )
    })
  }
  
  renderRadioButtons() {
    const { options, cssBlockName } = this.props;
    return options.map(option => {
      
      const id = option.get("id");
      const selected = id === this.getSelectedOptionID();
      
      const classNames = [ `${cssBlockName}__radio-button` ];
      if (selected) {
        classNames.push(`${cssBlockName}__radio-button--selected`);
      }
      
      return (
        <span key={option.get("id")} className={classNames.join(" ")} onClick={() => this.onRadioButtonChange(id)}>
          { option.get("name") }
        </span>
      );
      
    })
  }
  
  renderDropdown() {
    const { options, disabled, readOnly } = this.props;
    return (
      <Dropdown
        cssModifier="school_money"
        options={options}
        selectedOptionID={this.getSelectedOptionID()}
        onChange={this.onDropdownChange}
        placeholder="No Selection"
        readOnly={disabled || readOnly}
      />
    )
    // we provide out own styling for "disabled" state, so don't set it on the dropdown
  }
  
  /**
   * Delegate to either renderCheckboxes or renderDropdown, based on the `type` prop
   */
  renderType() {
    switch (this.props.type) {
      case inputTypes.CHECKBOXES:
        return this.renderCheckboxes();
      case inputTypes.RADIO_BUTTONS:
        return this.renderRadioButtons();
      case inputTypes.DROPDOWN:
        return this.renderDropdown();
      default:
        return null;
    }
  }
  
  render() {
    
    const { cssBlockName, disabled, readOnly, disabledPlaceholder } = this.props;
    const showPlaceholder = disabled && disabledPlaceholder !== undefined;
    
    const classNames = [ cssBlockName ];
    if (disabled) {
      classNames.push(`${cssBlockName}--disabled`);
    }
    if (readOnly) {
      classNames.push(`${cssBlockName}--read-only`);
    }
    
    return (
      <tr className={classNames.join(" ")}>
        <td className={`${cssBlockName}__weekday-cell`}>{this.props.weekdayName}</td>
        <td className={`${cssBlockName}__input-cell`} onClick={this.onInputClick}>
          { showPlaceholder ? disabledPlaceholder : this.renderType() }
        </td>
        <td className={`${cssBlockName}__price-cell`}>
          <Currency amount={this.getSelectedOptionPrice()} />
        </td>
      </tr>
    )
    
  }
  
}



// WEBPACK FOOTER //
// ./src/components/school_money/DailyBookingInput.js